/**
 * Client-side obra name matching used to SUGGEST an existing obra in the guia
 * form (the user always confirms, so this can be a bit more aggressive than the
 * server's conservative auto-consolidation).
 *
 * Two names are considered the same when, after normalization (lowercase,
 * accent-stripped, leading numeric codes dropped), the shorter token set is a
 * subset of the longer one — so "Supercor" ≡ "Obra Supercor" ≡ "SuperCor Fluvial",
 * and "Start Campus" ≡ "StartCampus" (via the compact comparison).
 */

const DIACRITICS = /[̀-ͯ]/g;

function stripDiacritics(s: string): string {
  return s.normalize('NFD').replace(DIACRITICS, '');
}

function tokens(s: string): string[] {
  const norm = stripDiacritics(s).toLowerCase();
  const toks = norm.split(/[\s\-/.,;:]+/).filter(Boolean);
  let i = 0;
  while (i < toks.length && /^\d+$/.test(toks[i])) i++; // drop leading numeric codes
  return toks.slice(i);
}

function compact(s: string): string {
  return stripDiacritics(s)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

export function isSameObra(a: string, b: string): boolean {
  const ca = compact(a);
  const cb = compact(b);
  if (ca.length > 0 && ca === cb) return true;

  const ta = tokens(a);
  const tb = tokens(b);
  if (ta.length === 0 || tb.length === 0) return a.trim().toLowerCase() === b.trim().toLowerCase();

  const [shorter, longer] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  return shorter.every((tok) => longer.includes(tok));
}

/** Returns the best existing obra name matching `input`, preferring the shortest (base) name; null if none. */
export function findObraMatch(input: string, obras: string[]): string | null {
  const matches = obras.filter((o) => isSameObra(input, o));
  if (matches.length === 0) return null;
  return matches.sort((a, b) => tokens(a).length - tokens(b).length || a.length - b.length)[0];
}
